/**
 * The airport systems dashboard — all thirty boards.
 *
 * The demo's `dashDraw()` renders the whole screen into `#dashMain`: the
 * emergency and alarm bars, the group and board navigation, the tile style and
 * ordering controls, and whichever board is selected. It also wires its own
 * handlers afterwards, including the nav — so switching boards works without
 * React knowing anything about it.
 *
 * This replaced a hand-written Overview. That version reproduced one board's
 * markup by hand and left the other twenty-nine unreachable; worse, a hand copy
 * of a board is a second thing to keep in step with the demo. Driving the
 * original renderer gives every board at once and cannot drift.
 *
 * React's only jobs here are the frame and redrawing when the estate changes.
 */
import { useEffect, useRef } from "react";
import { DASHGROUPS, dashDraw, setDASHGROUP, setDASHVIEW } from "../domain/generated";
import { useRepaint } from "../lib/repaint";
import { useShellElement } from "../domain/domHost";
import { t } from "../../i18n";
import "../styles/demo.css";

/**
 * `view` opens one board on its own — the route for that board passes its
 * DASHVIEWS id (see `dashboardScreens.ts`). The board is drawn by the same
 * renderer, with its group set so the renderer agrees with the board on show,
 * but everything the dashboard puts around a board — the header, the group
 * tabs, the tile and order controls, the alarm bars, the row of sibling boards
 * and its subtitle — is hidden (`embed.css`): a person who chose SMS from the
 * menu asked for SMS, not the whole dashboard around it. The alarms are still
 * on the systems dashboard, where they belong, and the sibling boards are
 * screens of their own.
 *
 * Without a view this is the systems dashboard, on whichever board it was
 * last on.
 */
export function Dashboard({ view }: { view?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const wrap = useRef<HTMLDivElement | null>(null);

  useShellElement("dashEl", wrap);
  useRepaint(dashDraw);

  useEffect(() => {
    if (view) {
      setDASHVIEW(view);
      const group = (DASHGROUPS as { k: string; views: string[] }[]).find((g) => g.views.includes(view));
      setDASHGROUP(group?.k ?? DASHGROUPS[0].k);
    }
    /*
     * dashDraw finds #dashMain by id, so it can only run once the element is
     * in the document — which is why this is an effect and not a render.
     */
    if (ref.current) dashDraw();
  }, [view]);

  return (
    <div id="dash" data-i18n-dom className={view ? "on boardOnly" : "on"} ref={wrap}>
      <div className="t-head">
        <b>
          <span className="co">SKY LOGICS</span> · {t("DYNAMIC DASHBOARD")}
        </b>
      </div>
      {/* every board, and the navigation between them, is written in here */}
      <div id="dashMain" ref={ref}></div>
    </div>
  );
}
